import React, { useMemo } from 'react';
import { Button, Paper } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { useSummaryStepStyles } from '../../CreateActivityAndPricing/Summary/SummaryStepStyles';
import { SUMMARY_STATUS } from '../../CreateActivityAndPricing/Summary/SummaryStepConsts';
import { buildCreateBundleValidationData, CREATE_BUNDLE_STEPS } from '../createBundleConsts';
import { useGetAllActivityItemsQuery } from 'features/activity/activitySlice';
import { arrayToObject } from 'utils/object-util';
import LoadingSpinner from 'components/LoadingSpinner';
import { isBundleStepModified, hasAnyBundleStepChanges } from '../utils/bundleSaveOperations';

const BundleSummaryStep = ({ onEditStep }) => {
    const classes = useSummaryStepStyles();
    const steps = useSelector(state => state.bundleState.steps);
    const isEditMode = useSelector(state => state.bundleState.isEditMode);
    const originalSteps = useSelector(state => state.bundleState.originalSteps);
    const formData = buildCreateBundleValidationData(steps);

    const { data: activityItems, isLoading } = useGetAllActivityItemsQuery();
    const activityItemsById = useMemo(() => arrayToObject(activityItems), [activityItems]);

    const formatBundleDetails = () => {
        const details = [];
        const bundle = formData.bundleDetails;

        if (bundle?.name) details.push(bundle.name);

        const selectedIds = bundle?.activityItemIds || [];
        if (selectedIds.length > 0) {
            const names = selectedIds
                .map(id => activityItemsById[id]?.name)
                .filter(Boolean)
                .join(', ');
            if (names) details.push(`Activities: ${names}`);
        }

        return details.join(' • ');
    };

    const formatPricingDetails = () => {
        const pricingCount = formData?.pricing?.pricingItems?.length || 0;
        return pricingCount > 0
            ? `${pricingCount} pricing${pricingCount > 1 ? 's' : ''} configured`
            : '0';
    };

    const formatAutomationDetails = () => {
        const rulesCount = formData?.rules?.rulesList?.length || 0;
        return rulesCount > 0
            ? `${rulesCount} rule${rulesCount > 1 ? 's' : ''} configured`
            : `${rulesCount}`;
    };

    if (isLoading) {
        return <LoadingSpinner loading={true} />;
    }

    const isModified = (stepIndex) => {
        if (!isEditMode) return false;
        return isBundleStepModified(originalSteps, steps, stepIndex);
    };

    const hasAnyChanges = isEditMode && hasAnyBundleStepChanges(originalSteps, steps);

    const isBundleConfigured = CREATE_BUNDLE_STEPS[0].validate(formData);
    const isPricingConfigured = CREATE_BUNDLE_STEPS[1].validate(formData);
    const isRuleConfigured = CREATE_BUNDLE_STEPS[2].validate(formData);

    const summaryCards = [
        {
            title: 'Bundle',
            status: isEditMode
                ? (isModified(0) ? SUMMARY_STATUS.MODIFIED : SUMMARY_STATUS.NOT_MODIFIED)
                : (isBundleConfigured ? SUMMARY_STATUS.CONFIGURED : SUMMARY_STATUS.SKIPPED),
            details: isBundleConfigured ? formatBundleDetails() : '',
            stepIndex: 0
        },
        {
            title: 'Pricing',
            status: isEditMode
                ? (isModified(1) ? SUMMARY_STATUS.MODIFIED : SUMMARY_STATUS.NOT_MODIFIED)
                : (isPricingConfigured ? SUMMARY_STATUS.CONFIGURED : SUMMARY_STATUS.SKIPPED),
            details: isPricingConfigured ? formatPricingDetails() : '',
            stepIndex: 1
        },
        {
            title: 'Automation',
            status: isEditMode
                ? (isModified(2) ? SUMMARY_STATUS.MODIFIED : SUMMARY_STATUS.NOT_MODIFIED)
                : (isRuleConfigured ? SUMMARY_STATUS.CONFIGURED : SUMMARY_STATUS.SKIPPED),
            details: isRuleConfigured ? formatAutomationDetails() : '',
            stepIndex: 2
        }
    ];

    return (
        <div className={classes.container}>
            <div className={classes.headerSection}>
                <div className={classes.title}>
                    {isEditMode ? 'Ready to save' : 'Ready to create'}
                </div>
                <div className={classes.subtitle}>
                    {isEditMode
                        ? (hasAnyChanges
                            ? 'Review your changes before saving.'
                            : 'No changes have been made yet.')
                        : 'Review your configuration before creating the bundle.'}
                </div>
            </div>
            <div className={classes.cardsList}>
                {summaryCards.map((card) => (
                    <Paper key={card.stepIndex} className={classes.summaryCard}>
                        <div className={classes.cardContent}>
                            <div className={classes.cardInfo}>
                                <div className={classes.cardHeader}>
                                    <span className={classes.cardTitle}>{card.title}</span>
                                    <span className={
                                        card.status === SUMMARY_STATUS.CONFIGURED
                                            ? classes.statusConfigured
                                            : card.status === SUMMARY_STATUS.MODIFIED
                                                ? classes.statusModified
                                                : card.status === SUMMARY_STATUS.NOT_MODIFIED
                                                    ? classes.statusNotModified
                                                    : classes.statusSkipped
                                    }>
                                        {card.status}
                                    </span>
                                </div>
                                <div className={classes.cardDetails}>
                                    {card.details || ''}
                                </div>
                            </div>
                            <Button
                                className={classes.editButton}
                                onClick={() => onEditStep(card.stepIndex)}
                            >
                                Edit
                            </Button>
                        </div>
                    </Paper>
                ))}
            </div>
        </div>
    );
};

export default BundleSummaryStep;
